import { AssessmentMethodEnum, LikelihoodColorLevel } from 'src/app/models/risk-assessment.model';
import { StatisticsEnum } from 'src/app/enums/assessmentStatus.enum';
import { getEnumList } from 'src/app/_helpers/enum-helper';

/**
 * Minimal shape the risk-statistic resolvers read from a register row. Both the fully-typed
 * `RiskRegister` and the looser per-agent register entries used by the Risk Register report
 * satisfy this, so the same resolution logic serves the on-screen table, the Excel export and
 * the templatized PDF (DASH-2567 on top of the DASH-2387 report).
 */
export interface LikelihoodResolvableRegister {
    methodOfAssessment?: unknown;
    qualitativeExposureScore?: unknown;
    qualitativeFrequencyScore?: unknown;
    qualitativeLikelihoodScore?: unknown;
    quantitativeLikelihoodScore?: unknown;
    title?: unknown;
    [key: string]: unknown;
}

/**
 * Quantitative registers match the level by the backend likelihood score (keyed to the
 * likelihood table). Qualitative registers store a likelihood score derived from a separate
 * qualitative mapping that need not equal the table keys, so we instead locate the level whose
 * qualitative threshold range contains the exposure x frequency product (the same approach the
 * register detail popup uses), falling back to the score key.
 */
export function matchLikelihoodLevel(
    register: LikelihoodResolvableRegister,
    exposureLikelihoodLevels: LikelihoodColorLevel[],
): LikelihoodColorLevel | undefined {
    if (register.methodOfAssessment === AssessmentMethodEnum.Quantitative) {
        const score = Number(register.quantitativeLikelihoodScore);
        return Number.isFinite(score) && score !== 0
            ? exposureLikelihoodLevels.find(level => level.key === score)
            : undefined;
    }

    const product = Number(register.qualitativeExposureScore) * Number(register.qualitativeFrequencyScore);
    const byRange = Number.isFinite(product) && product > 0
        ? exposureLikelihoodLevels.find(level =>
            Number.isFinite(level.minQualThreshold) &&
            Number.isFinite(level.maxQualThreshold) &&
            product >= (level.minQualThreshold as number) &&
            product <= (level.maxQualThreshold as number))
        : undefined;

    if (byRange) {
        return byRange;
    }

    const score = Number(register.qualitativeLikelihoodScore);
    return Number.isFinite(score) && score !== 0
        ? exposureLikelihoodLevels.find(level => level.key === score)
        : undefined;
}

/**
 * Returns the configured likelihood level's Name (Likelihood) and QuantitativeName (Exposure
 * Band). The Exposure Band equates any assessment to its quantitative risk category, so it is
 * shown even for qualitative registers. Falls back to the translated N/A when no level matches.
 */
export function resolveLevelLabels(
    register: LikelihoodResolvableRegister,
    exposureLikelihoodLevels: LikelihoodColorLevel[],
    notAvailable: string,
): { name: string; band: string } {
    const matched = matchLikelihoodLevel(register, exposureLikelihoodLevels);
    return {
        name: matched?.Name?.trim() || notAvailable,
        band: matched?.QuantitativeName?.trim() || notAvailable,
    };
}

/**
 * Resolves the value shown for a single statistic cell. Likelihood renders the configured level
 * NAME and Exposure Band the configured QuantitativeName — both looked up from the same likelihood
 * level, never the raw numeric score (DASH-2567). Method of assessment renders the register title;
 * every other key is a real register property read verbatim.
 */
export function getRegisterStatisticValue(
    register: LikelihoodResolvableRegister,
    statKey: string,
    exposureLikelihoodLevels: LikelihoodColorLevel[],
    notAvailable: string,
): string {
    if (statKey === 'methodOfAssessment') {
        return register.title != null ? String(register.title) : '-';
    }
    if (statKey === 'likelihood') {
        return resolveLevelLabels(register, exposureLikelihoodLevels, notAvailable).name;
    }
    if (statKey === 'exposureBand') {
        return resolveLevelLabels(register, exposureLikelihoodLevels, notAvailable).band;
    }
    const value = register[statKey];
    return value == null ? '-' : String(value);
}

/** Builds the display value for every StatisticsEnum key on a single register, keyed by stat key. */
export function buildRegisterStatisticValues(
    register: LikelihoodResolvableRegister,
    exposureLikelihoodLevels: LikelihoodColorLevel[],
    notAvailable: string,
): { [statKey: string]: string } {
    return getEnumList(StatisticsEnum).reduce((values, statKey) => {
        values[statKey] = getRegisterStatisticValue(register, statKey, exposureLikelihoodLevels, notAvailable);
        return values;
    }, {} as { [statKey: string]: string });
}
